import MeshUtils from '@/commons/MeshUtils';
import AnimatedCamera from '@/commons/camera/AnimatedCamera';
import CustomOrbitControls from '@/commons/camera/controls/CustomOrbitControls';
import { Box3, Object3D, Vector3 } from 'three';
import { Easing, Tween } from '@tweenjs/tween.js';
import type { ICameraControls } from '@/commons/camera/controls/ICameraControls';
import type { TCameraParams } from '@/commons/camera/TCameraParams';

class ArcRotateCamera extends AnimatedCamera {
    isPan: boolean = false;
    _pollOpen = false;
    mouseStartPosition: Vector3 = new Vector3();
    camAnimation: Tween<{ t: number }> | null = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _zoomToFitAnimation: Tween<any> | null = null;

    get pollOpen() {
        return this._pollOpen;
    }

    set pollOpen(value: boolean) {
        this._pollOpen = value;
        if (value) {
            this.controls.maxDistance = 2.5;
        } else {
            this.controls.maxDistance = 0.95;
        }
    }

    // #region INITIALIZATION
    protected createControls(rendererDomElement: HTMLCanvasElement): ICameraControls {
        return new CustomOrbitControls(this, rendererDomElement);
    }

    constructor(cameraParams: TCameraParams) {
        super(cameraParams);
        this.initializeControls();
        this.initCameraListener();
    }

    initCameraListener() {
        this.controls.domElement?.addEventListener('mousedown', this.mouseDownCallback);
        this.controls.domElement?.addEventListener('mousemove', this.moveCallback);
        this.controls.domElement?.addEventListener('mouseup', this.mouseUpCallback);
        this.controls.domElement?.addEventListener('touchstart', this.mouseDownCallback);
        this.controls.domElement?.addEventListener('touchmove', this.moveCallback);
        this.controls.domElement?.addEventListener('touchend', this.mouseUpCallback);
    }
    // #endregion

    // #region CALLBACKS
    mouseDownCallback = (e: MouseEvent | TouchEvent) => {
        if (
            (e.type === 'mousedown' && (e as MouseEvent)?.button !== 0) ||
            (e.type === 'touchstart' && (e as TouchEvent)?.touches.length > 1)
        ) {
            this.isPan = false;
            this.controls.enableDamping = true;
            return;
        }
        const { width, height } = this.controls.domElement!.getClientRects()[0];
        const x = e.type === 'touchstart' ? (e as TouchEvent).touches[0].clientX : (e as MouseEvent).clientX;
        const y = e.type === 'touchstart' ? (e as TouchEvent).touches[0].clientY : (e as MouseEvent).clientY;
        const mouseToWorld = MeshUtils.mouseToWorld(x, y, { width, height, aspect: width / height }, this, new Vector3(), this.radius);
        this.mouseStartPosition.copy(mouseToWorld);
        this.isPan = true;
    };

    zoomOnSelection = (objectSelection: Object3D[], offset: number = 0): Promise<void> => {
        const box = new Box3();
        for (let i = 0; i < objectSelection.length; i += 1) {
            box.expandByObject(objectSelection[i]);
        }
        const target = new Vector3();
        box.getCenter(target);
        const duration = 750;
        const finalPosition = MeshUtils.fitCameraToSelection(this, target, objectSelection, 0);
        const convPosition = MeshUtils.sphericalFromCartesian(finalPosition.x, target.y, finalPosition.z);
        if (offset) {
            convPosition.radius *= offset;
        }
        const anim = {
            t: 0,
            radius: this.radius,
            posY: this.position.y,
        };
        return new Promise<void>((resolve) => {
            const ending = () => {
                this._zoomToFitAnimation = null;
                resolve();
            };

            this._zoomToFitAnimation = new Tween(anim)
                .to({ t: 1, radius: convPosition.radius, posY: finalPosition.y }, duration)
                .onUpdate(() => {
                    this.position.copy(new Vector3(0, 0, anim.radius).applyQuaternion(this.quaternion)).add(this.controls.target);
                    this.controls.target.copy(this.controls.target.clone().lerp(target, anim.t));
                })
                .onComplete(ending)
                .easing(Easing.Quadratic.InOut)
                .start();
        });
    };

    animCameraToNewPosition({ radius, target }: { target: Vector3; radius: number }) {
        return new Promise((resolve) => {
            const ending = () => {
                this.camAnimation = null;
                resolve(true);
            };
            const duration = 1250;
            const anim = { t: 0, y: this.controls.target.y, radius: this.radius };
            this.camAnimation = new Tween(anim)
                .to({ t: 1, y: target.y, radius }, duration)
                .onUpdate(() => {
                    this.setRadius(anim.radius);
                    this.controls.target.y = anim.y;
                })
                .onComplete(ending)
                .easing(Easing.Quadratic.InOut)
                .start();
        });
    }

    mouseUpCallback = () => {
        this.isPan = false;
    };

    get panLimit() {
        return this.pollOpen ? -0.1 : 0.7028532044004389 - 0.2 - (1 - this.radius) * 0.1;
    }

    moveCallback = (e: MouseEvent | TouchEvent) => {
        if (!this.isPan) return;
        const camHeight = 0.7028532044004389;
        const panOffset = 0.2;
        const { width, height } = this.controls.domElement!.getClientRects()[0];
        const x = e.type === 'touchmove' ? (e as TouchEvent).touches[0].clientX : (e as MouseEvent).clientX;
        const y = e.type === 'touchmove' ? (e as TouchEvent).touches[0].clientY : (e as MouseEvent).clientY;
        const newMouse = MeshUtils.mouseToWorld(x, y, { width, height, aspect: width / height }, this, new Vector3(), this.radius);
        const sign = Math.sign(newMouse.y - this.mouseStartPosition.y);
        const zoomingFactor = (1 - this.radius) * 0.1;
        const moveVector =
            (((new Vector3(0, this.mouseStartPosition.y, 0).distanceTo(new Vector3(0, newMouse.y, 0)) / height) * 0.025) / this.radius) *
            sign;
        const newValue = Math.min(Math.max(this.position.y + moveVector, this.panLimit), camHeight + panOffset + zoomingFactor);
        this.position.y = newValue;
        this.controls.target.y = newValue;
        this.mouseStartPosition.copy(newMouse);
    };
    // #endregion

    dispose(): void {
        this.controls.domElement?.removeEventListener('mousedown', this.mouseDownCallback);
        this.controls.domElement?.removeEventListener('mousemove', this.moveCallback);
        this.controls.domElement?.removeEventListener('mouseup', this.mouseUpCallback);

        this.controls.domElement?.removeEventListener('touchstart', this.mouseDownCallback);
        this.controls.domElement?.removeEventListener('touchmove', this.moveCallback);
        this.controls.domElement?.removeEventListener('touchend', this.mouseUpCallback);

        this.controls.dispose();
    }

    render(): void {
        super.render();
        this._zoomToFitAnimation?.update();
    }
}

export default ArcRotateCamera;
